/*
 OK FBI — Public Legal Pages
 Routes: /terms  /privacy
 Public. No auth required. RTL Arabic. Black + Gold identity.
 Content is original — NOT copied from TikTok or any third party.
*/
import React from "react";
import { Link } from "react-router-dom";

const LAST_UPDATED = "11 فبراير 2026";

// ---- Neutral OK FBI wordmark (SVG, no external font dependency) ----
function Wordmark() {
  return (
    <div className="inline-flex items-baseline gap-2 select-none">
      <span
        className="text-2xl font-black tracking-[0.35em]"
        style={{
          color: "#E8D08A",
          textShadow: "0 1px 0 rgba(0,0,0,0.85), 0 0 12px rgba(197,160,89,0.35)",
          fontFamily: "ui-monospace, monospace",
        }}
      >
        OK<span style={{ color: "#7a5a1c" }}>·</span>FBI
      </span>
      <span className="text-[10px] text-zinc-500 tracking-widest">INVESTIGATION</span>
    </div>
  );
}

// ---- Shared header/footer chrome for public legal pages ----
function LegalChrome({ children, title, subtitle }) {
  return (
    <div dir="rtl" className="relative min-h-screen w-full bg-[#050505] text-white" data-testid="legal-chrome">
      {/* Subtle grain / vignette layer */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(80% 60% at 50% 0%, rgba(197,160,89,0.08) 0%, rgba(0,0,0,0) 60%), linear-gradient(180deg, #050505 0%, #0a0a0a 100%)",
        }}
      />
      <header className="relative z-10 border-b border-[#1a1a1a] bg-black/60 backdrop-blur-sm">
        <div className="max-w-[1080px] mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="hover:opacity-90 transition-opacity" data-testid="legal-home-link">
            <Wordmark />
          </Link>
          <nav className="text-[11px] font-cairo tracking-[0.25em] text-zinc-400 flex items-center gap-4">
            <Link to="/terms" className="hover:text-gold transition-colors" data-testid="nav-terms">شروط الاستخدام</Link>
            <span className="text-zinc-700">·</span>
            <Link to="/privacy" className="hover:text-gold transition-colors" data-testid="nav-privacy">سياسة الخصوصية</Link>
          </nav>
        </div>
      </header>

      <main className="relative z-10 max-w-[880px] mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <div className="mb-8">
          <div className="text-[10px] tracking-[0.5em] text-gold font-mono-ar mb-2">{subtitle}</div>
          <h1 className="text-3xl sm:text-4xl font-cairo font-black text-white leading-tight">{title}</h1>
          <div className="mt-3 text-[11px] text-zinc-500 font-cairo">
            آخر تحديث: <span className="text-zinc-300">{LAST_UPDATED}</span>
          </div>
          <div
            className="mt-5 h-px w-24"
            style={{ background: "linear-gradient(90deg, #C5A059 0%, rgba(197,160,89,0) 100%)" }}
          />
        </div>

        <div
          className="prose-legal text-zinc-300 text-[14px] sm:text-[15px] leading-[1.95] font-cairo"
          style={{ letterSpacing: "0.005em" }}
        >
          {children}
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}

// ---- Public footer with legal + fiction disclaimer (shared) ----
export function PublicFooter() {
  return (
    <footer className="relative z-10 border-t border-[#1a1a1a] bg-black/60 mt-14" data-testid="public-footer">
      <div className="max-w-[1080px] mx-auto px-5 sm:px-8 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="text-[10px] text-zinc-500 font-cairo max-w-[640px] leading-relaxed" data-testid="footer-disclaimer">
          OK FBI لعبة تحقيق تفاعلية خيالية لأغراض الترفيه. جميع القضايا والشخصيات والأسماء والأحداث والأدلة داخل اللعبة
          من وحي الخيال، وأي تشابه مع أشخاص أو أحداث حقيقية هو محض مصادفة. OK FBI ليست جهة حكومية أو أمنية ولا ترتبط
          أو تتبع مكتب التحقيقات الفيدرالي (FBI) أو أي جهة رسمية.
        </div>
        <div className="flex items-center gap-4 text-[11px] font-cairo tracking-[0.25em] shrink-0">
          <Link to="/terms" className="text-zinc-400 hover:text-gold transition-colors" data-testid="footer-terms">شروط الاستخدام</Link>
          <span className="text-zinc-700">·</span>
          <Link to="/privacy" className="text-zinc-400 hover:text-gold transition-colors" data-testid="footer-privacy">سياسة الخصوصية</Link>
        </div>
      </div>
      <div className="max-w-[1080px] mx-auto px-5 sm:px-8 pb-6 text-[10px] text-zinc-600 font-mono-ar tracking-widest">
        © {new Date().getFullYear()} OK FBI — جميع الحقوق محفوظة
      </div>
    </footer>
  );
}

// Small reusable heading + section
function H2({ children, id }) {
  return (
    <h2
      id={id}
      className="text-white text-lg sm:text-xl font-cairo font-black mt-9 mb-3 pb-2"
      style={{ borderBottom: "1px solid rgba(197,160,89,0.25)" }}
    >
      {children}
    </h2>
  );
}
function P({ children }) { return <p className="mb-3">{children}</p>; }
function UL({ children }) { return <ul className="list-disc pr-6 mb-4 space-y-1.5">{children}</ul>; }

// ================================================================
// TERMS OF SERVICE
// ================================================================
export function TermsPage() {
  return (
    <LegalChrome title="شروط الاستخدام" subtitle="OK · FBI · TERMS">
      <P>
        مرحباً بك في <b>OK FBI</b>. باستخدامك المنصة أو أي جزء منها فأنت توافق على هذه الشروط. إن كنت غير موافق،
        يرجى التوقّف عن استخدام المنصة.
      </P>

      <H2>1. طبيعة المنصة</H2>
      <P>
        OK FBI منصة ألعاب تحقيق تفاعلية وترفيهية عبر الإنترنت. تُوفّر تجربة محاكاة لعمل المحققين تشمل استعراض القضايا،
        فحص الأدلة، مراجعة السجلات، تسجيل النظريات، التصويت، ومتابعة التقدم داخل نظام المحققين.
      </P>

      <H2>2. الحسابات وأرقام المحققين</H2>
      <UL>
        <li>تسجيل الحساب يتم عبر مصادقة TikTok الرسمية بعد إعدادها من قِبل الإدارة.</li>
        <li>لكل حساب مُعرّف رقمي داخلي (Agent ID) يُصدره النظام. بعض الأرقام محجوزة للإدارة ولا تُمنح للاعبين.</li>
        <li>المستخدم مسؤول عن الحفاظ على سرّية بيانات الدخول الخاصة به.</li>
        <li>يُمنع منعاً باتاً انتحال هوية مستخدم آخر أو التسجيل نيابة عن الغير.</li>
      </UL>

      <H2>3. تقدم اللعب والعناصر الافتراضية</H2>
      <UL>
        <li>قد يشمل النظام: نقاط خبرة (XP)، مستويات (Levels)، رتب (Ranks)، انتصارات (Wins)، شارات، إنجازات، ملفات محقق، وإحصائيات.</li>
        <li>هذه العناصر افتراضية بالكامل داخل المنصة، ولا تمثّل أموالاً حقيقية أو قيمة مالية قابلة للسحب أو التحويل أو الاسترداد،
          إلا إذا نُصّ صراحةً على خلاف ذلك بتحديث مستقبلي للشروط.</li>
        <li>بعض الخصائص أو الباقات أو الشارات قد تختلف بحسب نوع العضوية والصلاحيات التي تحدّدها إدارة OK FBI (OWNER).</li>
        <li>قد تُضاف عناصر أو باقات أو خصائص أو تُعدَّل أو تُزال في المستقبل. أي وصف لخاصية مستقبلية لا يُعتبر التزاماً بتوفيرها.</li>
      </UL>

      <H2>4. الاشتراكات والمدفوعات</H2>
      <P>
        لا توجد حالياً أي عمليات دفع أو شراء أو اشتراكات مدفوعة مُفعّلة في OK FBI. أي ذكر لخطط مدفوعة داخل الواجهة هو
        عرض تصوّري لخصائص مستقبلية ولا يُعدّ عرضاً تعاقدياً أو التزاماً بالتفعيل. عند تفعيل أي منتج مدفوع لاحقاً ستُحدَّث
        هذه الشروط وتُعرَض تفاصيل الرسوم والسياسات المتعلقة بها بوضوح.
      </P>

      <H2>5. الاستخدام المقبول</H2>
      <UL>
        <li>يُمنع محاولة اختراق المنصة أو تعطيلها أو تجاوز الصلاحيات أو الوصول غير المصرَّح به إلى أنظمتها.</li>
        <li>يُمنع التلاعب بنتائج التصويت أو نقاط XP أو الانتصارات أو Agent IDs أو الرتب أو الشارات أو نتائج القضايا بأي أسلوب.</li>
        <li>يُمنع استخدام بوتات أو نصوص آلية أو أدوات خارجية لتضخيم النتائج داخل النظام.</li>
        <li>يُمنع استخدام لغة مسيئة أو محتوى مخالف للقانون داخل المنصة.</li>
        <li>يُمنع محاولة الحصول على بيانات مستخدمين آخرين بطرق غير مشروعة أو بأسلوب هندسة اجتماعية.</li>
      </UL>

      <H2>6. تعليق أو إيقاف الحساب</H2>
      <P>
        يحق لإدارة OK FBI تعليق أو إيقاف أي حساب يُخالف هذه الشروط أو يسيء استخدام النظام أو يمثّل خطراً على سلامة
        المنصة أو المستخدمين، وذلك دون إشعار مسبق عند الحاجة.
      </P>

      <H2>7. تنبيه المحتوى الخيالي (Fictional Content Disclaimer)</H2>
      <P>
        OK FBI لعبة تحقيق تفاعلية خيالية لأغراض الترفيه فقط. ما يلي مبادئ أساسية تحكم كل محتوى داخل المنصة:
      </P>
      <UL>
        <li>جميع القضايا الموجودة داخل اللعبة <b>خيالية</b> ومن وحي الخيال.</li>
        <li>جميع الجرائم المعروضة داخل القضايا <b>خيالية</b>.</li>
        <li>جميع الأحداث والسيناريوهات <b>خيالية</b>.</li>
        <li>جميع الأدلة والتقارير والوثائق والمحادثات والتحقيقات داخل القضايا <b>خيالية</b> ومُعدّة لأغراض اللعبة.</li>
        <li>جميع أسماء الشخصيات المرتبطة بالقضايا <b>خيالية</b>.</li>
        <li>جميع المشتبه بهم والضحايا والشهود والشخصيات المرتبطة بالقضايا <b>شخصيات خيالية</b>.</li>
        <li>الصور والوجوه والهويات المستخدمة لشخصيات القضايا يجب أن تكون <b>مولّدة بالذكاء الاصطناعي</b> أو مصمَّمة خصيصاً
          لأغراض اللعبة، ولا يُقصَد بها تمثيل أشخاص حقيقيين.</li>
        <li>أسماء الشركات والمؤسسات والعناوين وأرقام الهواتف وأرقام المركبات وأرقام الملفات والوثائق والبيانات التعريفية
          المستخدمة داخل القضايا يجب أن تكون خيالية أو مُعدّة خصيصاً للعبة عندما يمكن أن توحي بهوية حقيقية.</li>
        <li>أي تشابه غير مقصود مع أشخاص حقيقيين، أحياء أو متوفّين، أو أحداث أو جهات حقيقية، هو محض مصادفة.</li>
        <li>لا يجوز استخدام النظام لإنشاء اتهامات جنائية ضد أشخاص حقيقيين.</li>
        <li>لا يجوز إنشاء قضية داخل اللعبة باستخدام بيانات شخصية حقيقية لشخص بهدف تصويره كمشتبه به أو مجرم أو ضحية.</li>
        <li>لا يجوز استخدام أسماء أو صور أو معلومات تعريفية حقيقية داخل القضايا بدون أساس قانوني وموافقة مناسبة.</li>
        <li>OK FBI <b>لا يجري</b> تحقيقات حقيقية، ولا يقدّم خدمات أمنية أو قانونية، وليس جهة إنفاذ قانون.</li>
        <li>OK FBI ليست جهة حكومية أو أمنية، وهي <b>غير تابعة أو معتمَدة أو مرتبطة</b> بمكتب التحقيقات الفيدرالي الأمريكي (FBI)
          أو أي جهة حكومية أو أمنية أخرى. استخدام «FBI» ضمن اسم اللعبة وهويتها البصرية لا يعني وجود أي ارتباط أو اعتماد رسمي.</li>
      </UL>

      <H2>8. حقوق الملكية الفكرية</H2>
      <P>
        الاسم OK FBI، وهويّته البصرية، والقضايا والأدلة والشخصيات والنصوص والتصاميم والواجهات المطوَّرة من قِبل فريق
        OK FBI مملوكة لأصحابها ومحمية بموجب قوانين الملكية الفكرية. لا يُسمح بنسخها أو إعادة استخدامها خارج المنصة دون
        إذن مسبق.
      </P>

      <H2>9. تحديد المسؤولية</H2>
      <P>
        تُقدَّم المنصة "كما هي" ودون ضمانات صريحة أو ضمنية بشأن التوفّر الدائم أو خلوّها من الأخطاء. لا تتحمّل OK FBI
        مسؤولية أي ضرر مباشر أو غير مباشر ناتج عن استخدام المنصة بما يتجاوز الحدود القانونية المسموح بها.
      </P>

      <H2>10. تعديل الشروط</H2>
      <P>
        يجوز تحديث هذه الشروط في أي وقت. النسخة الحالية تحمل تاريخ آخر تحديث المُبيَّن في أعلى الصفحة، والاستمرار في
        استخدام المنصة بعد التحديث يُعدّ موافقة على النسخة المُحدَّثة.
      </P>

      <H2>11. القانون الحاكم</H2>
      <P>
        تخضع هذه الشروط لأحكام القوانين المعمول بها في المنطقة التي تُدار منها المنصة، مع مراعاة الأحكام الإلزامية
        لبلد إقامة المستخدم عند الاقتضاء.
      </P>

      <H2>12. التواصل</H2>
      <P>
        يمكن التواصل مع إدارة OK FBI عبر قنوات التواصل الرسمية التي سيتم الإعلان عنها في الصفحة الرئيسية بعد تجهيزها.
      </P>
    </LegalChrome>
  );
}

// ================================================================
// PRIVACY POLICY
// ================================================================
export function PrivacyPage() {
  return (
    <LegalChrome title="سياسة الخصوصية" subtitle="OK · FBI · PRIVACY">
      <P>
        تحترم OK FBI خصوصية مستخدميها. توضّح هذه السياسة أنواع البيانات التي تُجمَع، وكيفية استخدامها، والحماية
        المطبَّقة عليها. تنطبق على الاستخدام المُصرَّح به للمنصة عبر الموقع الرسمي <span dir="ltr">okfbi.com</span>.
      </P>

      <H2>1. تسجيل الدخول عبر TikTok</H2>
      <P>
        بعد اكتمال إعداد <b>TikTok Login Kit</b> الرسمي، يستطيع المستخدم إنشاء حساب OK FBI وتسجيل الدخول باستخدام
        حسابه في TikTok، بعد موافقته الصريحة على شاشة الأذونات التي يعرضها TikTok. عند ذلك، ولأغراض إنشاء الحساب
        وتشغيله فقط، يستقبل OK FBI الحد الأدنى من المعلومات التي يُتيحها TikTok وفق الأذونات التي وافق عليها المستخدم،
        وتشمل عادةً ضمن نطاق <code className="text-gold">user.info.basic</code>:
      </P>
      <UL>
        <li>معرّف تقني ثابت (Open ID) يعيده TikTok، يُستخدم لربط تسجيل TikTok بالحساب الداخلي في OK FBI.</li>
        <li>اسم العرض العام (Display Name) عند إتاحته من TikTok.</li>
        <li>صورة الحساب (Avatar) عند إتاحتها من TikTok.</li>
      </UL>
      <P>
        لا يجمع OK FBI حالياً — من خلال TikTok — أياً ممّا يلي: رسائل TikTok، فيديوهات المستخدم، قائمة المتابعين، قائمة
        المتابَعين، تعليقات TikTok، بيانات البث المباشر، أو أي بيانات TikTok أخرى. إن أُضيف أي منها مستقبلاً بشكل فعلي
        وبصلاحيات رسمية إضافية، فسيتم تحديث هذه السياسة بذلك وسيُطلب من المستخدم موافقة جديدة عند الاقتضاء.
      </P>

      <H2>2. البيانات الداخلية التي يخزّنها OK FBI</H2>
      <UL>
        <li>رقم المحقق (Agent ID).</li>
        <li>الاسم الذي يظهر داخل المنصة (Display Name داخل OK FBI).</li>
        <li>Avatar المستخدَم داخل المنصة.</li>
        <li>مستوى (Level) ونقاط الخبرة (XP) والرتبة (Rank).</li>
        <li>الانتصارات (Wins) والشارات (Badges) والإنجازات (Achievements).</li>
        <li>تقدّم المستخدم في القضايا وحالات القضية.</li>
        <li>تصويت المستخدم داخل القضايا ونظرياته المحفوظة (Player Theories).</li>
        <li>بيانات العضوية / الباقة (Membership / Package information) عند وجودها.</li>
        <li>بيانات فنية وأمنية ضرورية لتشغيل الحساب والجلسة (مثل جلسة الدخول والطوابع الزمنية للنشاط
          وعنوان IP لغرض الحماية من الإساءة).</li>
      </UL>

      <H2>3. أغراض استخدام البيانات</H2>
      <UL>
        <li>إنشاء حساب المحقق وتشغيله.</li>
        <li>التحقّق من تسجيل الدخول واستمرار الجلسة بأمان.</li>
        <li>حفظ تقدّم المستخدم داخل القضايا.</li>
        <li>حفظ XP وحساب المستوى والرتبة والانتصارات.</li>
        <li>منح الشارات والإنجازات.</li>
        <li>تشغيل القضايا وحفظ تصويت المستخدم ونظرياته.</li>
        <li>تشغيل لوحة المتصدّرين (Leaderboard) والصفحات العامة للمحقق.</li>
        <li>حماية المنصة ومنع إساءة الاستخدام والاحتيال والتلاعب بالنتائج.</li>
      </UL>

      <H2>4. خصوصية بيانات TikTok الخاصة بك</H2>
      <P>
        لا يعرض OK FBI حساب TikTok الحقيقي الخاص بك للمستخدمين الآخرين داخل المنصة. لا يظهر للمستخدمين الآخرين من ملفك
        الشخصي أو عبر واجهات API العامة أيّ من التالي:
      </P>
      <UL>
        <li>اسم مستخدم TikTok الحقيقي (@handle).</li>
        <li>معرّف TikTok الداخلي (Open ID أو أي معرّف تقني).</li>
        <li>رابط ملف TikTok الشخصي.</li>
        <li>رمز الوصول (Access Token) أو رمز التحديث (Refresh Token).</li>
        <li>أي بيانات TikTok خاصة بك.</li>
      </UL>
      <P>
        المعلومات العامة التي قد يعرضها OK FBI للمستخدمين الآخرين مقتصرة على ما تُتيحه إعدادات المنصة، مثل:
        اسم العرض داخل OK FBI، رقم المحقق (Agent ID)، الـ Avatar المسموح بعرضه، المستوى، الرتبة، الانتصارات،
        الشارات والإنجازات، وحالة العضوية عندما تكون معروضة، والإحصائيات العامة التي يسمح بها النظام. لا يوجد داخل
        OK FBI نظام مراسلة أو تواصل يكشف حسابات TikTok الخاصة بين المحققين.
      </P>

      <H2>5. لا نبيع بياناتك</H2>
      <UL>
        <li>لا يبيع OK FBI البيانات الشخصية لأي طرف ثالث.</li>
        <li>لا يبيع OK FBI بيانات TikTok الخاصة بك.</li>
        <li>لا يُشارك OK FBI بيانات TikTok الخاصة بك مع مستخدمين آخرين على المنصة.</li>
      </UL>

      <H2>6. أمن المعلومات</H2>
      <P>
        يُطبِّق OK FBI إجراءات أمنية مناسبة لحماية البيانات، تشمل تشفير الجلسات ومفاتيح المصادقة، وحفظ الأسرار الحسّاسة
        (مثل مفاتيح خدمات الطرف الثالث) على الخادم فقط، وعدم كشفها في الواجهة الأمامية أو الواجهات العامة. ورغم ذلك،
        لا يمكن ضمان أمن كامل لأي نظام عبر الإنترنت.
      </P>

      <H2>7. الاحتفاظ بالبيانات</H2>
      <P>
        تُحفَظ البيانات فقط بالقدر الضروري لتشغيل الحساب والأغراض المشروعة المذكورة في هذه السياسة، وبما يتوافق مع
        المتطلبات القانونية عند وجودها.
      </P>

      <H2>8. حذف الحساب والبيانات</H2>
      <P>
        نعمل حالياً على إتاحة آلية طلب حذف الحساب والبيانات المرتبطة به من داخل المنصة. حتى اكتمال هذه الآلية، يمكن
        للمستخدم التواصل مع إدارة OK FBI لطلب الحذف. سيُعلَن عن قناة التواصل الرسمية في الصفحة الرئيسية عند اعتمادها.
      </P>

      <H2>9. حقوق المستخدم</H2>
      <UL>
        <li>الاطّلاع على البيانات التي يخزّنها OK FBI بخصوص حسابك.</li>
        <li>طلب تصحيح البيانات غير الدقيقة.</li>
        <li>طلب حذف الحساب والبيانات وفق ما تسمح به التشريعات المطبَّقة.</li>
        <li>سحب موافقة تسجيل TikTok من إعدادات حسابك على TikTok مباشرة، وسيؤدي ذلك إلى قطع الربط اللاحق مع OK FBI.</li>
      </UL>

      <H2>10. القاصرون</H2>
      <P>
        المنصة غير موجّهة للأطفال دون السن القانونية في بلد المستخدم لاستخدام خدمات الإنترنت التفاعلية. إذا اكتُشف
        وجود حساب يعود لقاصر بشكل يخالف الأنظمة المعمول بها، فسيتم إيقاف الحساب واتخاذ الإجراءات المناسبة.
      </P>

      <H2>11. تحديث سياسة الخصوصية</H2>
      <P>
        قد تُحدَّث هذه السياسة عند إضافة أذونات أو ميزات أو خدمات جديدة، أو عند تغيّر المتطلبات التنظيمية. النسخة
        الحالية تحمل تاريخ آخر تحديث المُبيَّن في أعلى الصفحة.
      </P>

      <H2>12. التواصل</H2>
      <P>
        لأي استفسار متعلق بالخصوصية، يمكن التواصل عبر قنوات التواصل الرسمية التي سيتم الإعلان عنها في الصفحة الرئيسية
        بعد تجهيزها.
      </P>
    </LegalChrome>
  );
}
